import { DateTime } from "luxon";

export type DateOperator = "after" | "before";

const DATE_OPERATOR_SEPARATOR = ":";

export function encodeDateOperatorValue(
  operator: DateOperator,
  isoDate: string,
): string {
  return `${operator}${DATE_OPERATOR_SEPARATOR}${isoDate}`;
}

export function isDateOperatorValue(value: string): boolean {
  return value.startsWith(`after${DATE_OPERATOR_SEPARATOR}`) || value.startsWith(`before${DATE_OPERATOR_SEPARATOR}`);
}

export function parseDateOperatorValue(
  value: string,
): { operator: DateOperator; isoDate: string } | null {
  if (!isDateOperatorValue(value)) {
    return null;
  }

  const separatorIndex = value.indexOf(DATE_OPERATOR_SEPARATOR);

  if (separatorIndex === -1) {
    return null;
  }

  const operator = value.slice(0, separatorIndex);
  const isoDate = value.slice(separatorIndex + DATE_OPERATOR_SEPARATOR.length);

  if ((operator === "after" || operator === "before") && isoDate) {
    return { operator, isoDate };
  }

  return null;
}

export function parseDateOperatorValues(values?: string[]): Partial<Record<DateOperator, string>> {
  if (!values?.length) {
    return {};
  }

  return values.reduce<Partial<Record<DateOperator, string>>>((accumulator, value) => {
    const parsedValue = parseDateOperatorValue(value);

    if (parsedValue) {
      accumulator[parsedValue.operator] = parsedValue.isoDate;
    }

    return accumulator;
  }, {});
}

export function hasDateOperatorValues(values?: string[]): boolean {
  return !!values?.some((value) => isDateOperatorValue(value));
}

export function upsertDateOperatorValue(
  values: string[] | undefined,
  operator: DateOperator,
  isoDate: string,
): string[] {
  const remainingValues = (values ?? []).filter((value) => {
    const parsedValue = parseDateOperatorValue(value);
    return parsedValue?.operator !== operator;
  });

  const nextValues = [...remainingValues, encodeDateOperatorValue(operator, isoDate)];

  return sortDateOperatorValues(nextValues);
}

export function removeDateOperatorValue(
  values: string[] | undefined,
  operator: DateOperator,
): string[] {
  return sortDateOperatorValues(
    (values ?? []).filter((value) => {
      const parsedValue = parseDateOperatorValue(value);
      return parsedValue?.operator !== operator;
    }),
  );
}

export function sortDateOperatorValues(values: string[]): string[] {
  const operatorOrder: Record<DateOperator, number> = {
    after: 0,
    before: 1,
  };

  return [...values].sort((left, right) => {
    const leftParsed = parseDateOperatorValue(left);
    const rightParsed = parseDateOperatorValue(right);

    if (!leftParsed || !rightParsed) {
      return left.localeCompare(right);
    }

    return operatorOrder[leftParsed.operator] - operatorOrder[rightParsed.operator];
  });
}

export function formatFilterIsoDate(isoDate: string): string {
  return DateTime.fromISO(isoDate, { zone: "utc" }).toFormat("M/d/yyyy");
}

export function getDateOperatorDisplayText(values: string[]): string {
  const parsedValues = parseDateOperatorValues(values);

  if (parsedValues.after && parsedValues.before) {
    return `between ${formatFilterIsoDate(parsedValues.after)} - ${formatFilterIsoDate(parsedValues.before)}`;
  }

  if (parsedValues.after) {
    return `after ${formatFilterIsoDate(parsedValues.after)}`;
  }

  if (parsedValues.before) {
    return `before ${formatFilterIsoDate(parsedValues.before)}`;
  }

  return "";
}